import React, { useMemo } from "react";
import { motion } from "framer-motion";
import styled from "styled-components";
import { Section } from "./sharedStyles";

const Wrap = styled(Section)`
  min-height: calc(100vh - 86px);
  padding: 72px 20px 96px;
  text-align: center;
  background: ${(p) => p.theme.gradients?.bgSection || p.theme.colors.bgDark};
`;
const Intro = styled.div`
  max-width: 720px;
  margin: 0 auto;
  h1 { margin: 0 0 10px; font-size: clamp(2rem, 5vw, 3.4rem); }
  p { margin: 0; color: ${(p) => p.theme.colors.muted}; }
`;
const Tree = styled.div`
  max-width: 1120px;
  margin: 58px auto 0;
`;
const Level = styled.div`
  position: relative;
  width: fit-content;
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  justify-content: center;
  gap: 22px;
  flex-wrap: wrap;
  padding-top: ${(p) => (p.first ? "0" : "48px")};
  &::before { content: ""; position: absolute; z-index: 0; top: 0; left: 50%; height: ${(p) => (p.first ? "0" : "48px")}; border-left: 2px dashed rgba(245,197,66,.48); }
  &::after { content: ""; position: absolute; z-index: 0; top: 48px; left: 120px; right: 120px; border-top: 2px dashed rgba(74,0,224,.42); display: ${(p) => (p.first || p.count < 2 ? "none" : "block")}; }
  @media (max-width: 679px) { width: 100%; gap: 0; &::after { display: none; } }
`;
const Card = styled(motion.article)`
  position: relative; z-index: 1; width: 240px; max-width: 100%; height: 220px; padding: 22px 18px; border-radius: 18px;
  display: flex; flex-direction: column; align-items: center; justify-content: flex-start;
  background: linear-gradient(180deg, rgba(255,255,255,.065), rgba(255,255,255,.025));
  border: 1px solid rgba(255,255,255,.1); box-shadow: 0 18px 42px rgba(0,0,0,.34);
  &::before { content: ""; position: absolute; top: -50px; left: 50%; height: 50px; border-left: 2px dashed rgba(245,197,66,.48); display: ${(p) => (p.first ? "none" : "block")}; }
  &::after { content: ""; position: absolute; z-index: 2; top: -6px; left: 50%; width: 10px; height: 10px; border-radius: 50%; transform: translateX(-50%); background: ${(p) => p.theme.colors.primary}; box-shadow: 0 0 0 4px ${(p) => p.theme.colors.bgDark}; display: ${(p) => (p.first ? "none" : "block")}; }
  h3 { margin: 0 0 6px; color: ${(p) => p.theme.colors.text}; }
  p { margin: 0; color: ${(p) => p.theme.colors.muted}; }
  small { display: block; margin-top: 6px; color: ${(p) => p.theme.colors.primary}; }
`;
const Avatar = styled.img`
  width: 78px; height: 78px; margin: 0 auto 14px; border-radius: 50%; object-fit: cover; border: 2px solid rgba(245,197,66,.55);
`;
const rank = (member) => {
  const position = (member.position || "").toLowerCase();
  if (position.includes("director")) return 0;
  if (position.includes("manager")) return 1;
  if (position.includes("lead")) return 2;
  return 3;
};

export default function Team({ members = [] }) {
  const levels = useMemo(() => {
    const grouped = members.reduce((result, member) => {
      const key = rank(member);
      result[key] = [...(result[key] || []), member];
      return result;
    }, {});
    return Object.keys(grouped).sort((a, b) => a - b).map((key) => grouped[key]);
  }, [members]);

  return (
    <Wrap id="team" aria-labelledby="team-heading">
      <Intro><h1 id="team-heading">Our Team</h1><p>Meet the people behind reliable, compliant recovery operations.</p></Intro>
      {levels.length ? <Tree aria-label="Team hierarchy">
        {levels.map((level, levelIndex) => <Level key={levelIndex} first={levelIndex === 0} count={level.length}>
          {level.map((member) => <Card key={member.id || member.name} first={levelIndex === 0} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.5, delay: levelIndex * 0.1 }}>
            {member.photo && <Avatar src={member.photo} alt={member.name} />}
            <h3>{member.name}</h3><p>{member.position}</p>
          </Card>)}
        </Level>)}
      </Tree> : <p style={{ color: "#A7B1C2", marginTop: 48 }}>Team profiles will appear here soon.</p>}
    </Wrap>
  );
}
